/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toSortParams } from '@data/dto/api.dto';
import { toBlocklist } from '@data/dto/blocklist.dto';
import { Meta, PaginateResult, SortParams } from '@data/models/api.interface';
import {
  Blocklist,
  BlocklistHttp,
  BlocklistWizard,
} from '@data/models/blocklist.interface';
import { DatatableReorderParams } from '@data/models/datatable.interface';
import { FilterParams } from '@data/models/filter.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class BlocklistService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly blocklistEndpoint = '/blocklist-repositories';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listBlocklists(
    filters: FilterParams,
  ): Promise<PaginateResult<Blocklist[]>> {
    const { data: response, error } = await this.API?.get<{
      data: BlocklistHttp[];
      meta: Meta;
    }>(this.blocklistEndpoint, filters);

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    return {
      items: response?.data?.map(toBlocklist) || [],
      totalSize: response?.meta.page.total || 0,
    };
  }

  public async upsertBlocklist({
    id,
    ...blocklistWizard
  }: BlocklistWizard): Promise<Blocklist | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<BlocklistWizard, 'id'> },
        { data: BlocklistHttp }
      >(this.blocklistEndpoint, { data: blocklistWizard });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      return toBlocklist(data?.data || {});
    }

    const { data, error } = await this.API?.patch<
      { data: BlocklistWizard },
      { data: BlocklistHttp }
    >(`${this.blocklistEndpoint}/${id}`, {
      data: { ...blocklistWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toBlocklist(data?.data || {});
  }

  public async deleteBlocklist(id: string): Promise<Blocklist | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error } = await this.API?.delete<{
      data: BlocklistHttp;
    }>(`${this.blocklistEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toBlocklist(data?.data || {});
  }

  public async sortBlocklist(
    sortParams: DatatableReorderParams<Blocklist>,
  ): Promise<Blocklist | void> {
    const {
      current: { id },
    } = sortParams;

    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.sorting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { error } = await this.API?.post<{ data: SortParams }, {}>(
      `${this.blocklistEndpoint}/${id}/sort`,
      {
        data: toSortParams('blocklist_repositories', sortParams),
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return sortParams.current;
  }
}
